import React, { useState, useCallback, useContext } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import { Trans, useTranslation } from "react-i18next";
import { ScreenSize, UI } from "@wwimmo/ui";
import { AssemblyAddRealestateList } from "src/components/assemblies/realestates/add-realestate-modal/AddAssemblyRealestateModal";
import { RootStoreContext } from "src/stores/RootStore";
import styles from "./AddRealestateModalList.module.css";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { uuid4 } from "@sentry/utils";
import { ErpType } from "src/network/User";

interface AddRealestateModalListProps {
    realestates: AssemblyAddRealestateList[];
}

const MINIMUM_CHARACTERS_FOR_SEARCH = 3;

const AddRealestateModalListBase = (props: AddRealestateModalListProps) => {
    const { t } = useTranslation();
    const { uiStore, assemblyStore, authStore } = useContext(RootStoreContext);
    const { realestates } = props;

    const [displayedRealestateItems, setDisplayedRealestateItems] =
        useState<Array<AssemblyAddRealestateList>>(realestates);
    const [searchQuery, setSearchQuery] = useState<string>("");

    const removeRealestateFromDisplayedList = useCallback(
        (realesateid: string) => {
            const newlyDisplayedRealestateItems = displayedRealestateItems.filter(
                (realestateItem) => realestateItem.id !== realesateid
            );
            setDisplayedRealestateItems([...newlyDisplayedRealestateItems]);
        },
        [displayedRealestateItems]
    );

    const onSearch = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const currentSearchQuery = e.target.value;
            setSearchQuery(currentSearchQuery);

            if (currentSearchQuery.length >= MINIMUM_CHARACTERS_FOR_SEARCH && realestates.length > 0) {
                const foundRealestates = realestates.filter((realestate) => {
                    return (
                        realestate.name?.toLowerCase().includes(currentSearchQuery.toLowerCase()) ||
                        realestate.number?.toLowerCase().includes(currentSearchQuery.toLowerCase())
                    );
                });

                setDisplayedRealestateItems([...foundRealestates]);
            } else {
                setDisplayedRealestateItems([...realestates]);
            }
        },
        [realestates]
    );

    const onAddRealestateToAssembly = useCallback(
        (realestateItem: AssemblyAddRealestateList) => async () => {
            const isIT2 = authStore.user?.erpType === ErpType.IT2;
            const isParent = isIT2 && !realestateItem.consolidationid;

            if (isParent) {
                // 1) remove the parent from display
                removeRealestateFromDisplayedList(realestateItem.id!);

                // 2) add the parent itself
                assemblyStore.addCurrentAssemblyRealestates({
                    id: uuid4(),
                    realestate: {
                        id: realestateItem.id!,
                        number: realestateItem.number!,
                        name: realestateItem.name!,
                        consolidationid: null
                    }
                });

                // 3) find & remove all its children, then add each
                displayedRealestateItems
                    .filter((item) => item.consolidationid === realestateItem.id)
                    .forEach((child) => {
                        removeRealestateFromDisplayedList(child.id!);

                        assemblyStore.addCurrentAssemblyRealestates({
                            id: uuid4(),
                            realestate: {
                                id: child.id!,
                                number: child.number!,
                                name: child.name!,
                                consolidationid: child.consolidationid!
                            }
                        });
                    });
            } else {
                // single‐item (non‐IT2 or a leaf)
                removeRealestateFromDisplayedList(realestateItem.id!);
                assemblyStore.addCurrentAssemblyRealestates({
                    id: uuid4(),
                    realestate: {
                        id: realestateItem.id!,
                        number: realestateItem.number!,
                        name: realestateItem.name!,
                        consolidationid: realestateItem.consolidationid ?? null
                    }
                });
            }
        },
        [authStore.user?.erpType, displayedRealestateItems, removeRealestateFromDisplayedList, assemblyStore]
    );

    const getColumnWidths = useCallback(() => {
        const columnWidthsDesktop = [1, 1, 1, 0.1];
        const columnWidthsLaptopL = [0.4, 0.8, 0.8, 0.4];
        const columnWidthsLaptop = [0.3, 0.9, 0.8, 0.4];
        const columnWidthsTablet = [0.2, 0.5, 0.5, 0];

        return uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP_L
            ? columnWidthsDesktop
            : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP_L
            ? columnWidthsLaptopL
            : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP
            ? columnWidthsLaptop
            : columnWidthsTablet;
    }, [uiStore.currentScreenSize]);

    const columnWidths = getColumnWidths();

    const renderItem = useCallback(
        (assemblyAddRealestate: AssemblyAddRealestateList, screenSize: ScreenSize) => {
            let listRows: React.ReactElement[] = [];

            switch (screenSize) {
                case ScreenSize.DESKTOP:
                    listRows = [
                        <UI.List.Row key={"r-1"}>
                            <UI.List.Cell
                                key={"c-1"}
                                colspan={columnWidths[0]}
                                value={
                                    <div
                                        className={
                                            assemblyAddRealestate.consolidationid === null ||
                                            assemblyAddRealestate.consolidationid === ""
                                                ? undefined
                                                : styles.ChildItemIT2
                                        }
                                        onClick={onAddRealestateToAssembly(assemblyAddRealestate)}
                                    >
                                        {assemblyAddRealestate.name}
                                    </div>
                                }
                            />
                            <UI.List.Cell
                                key={"c-2"}
                                colspan={columnWidths[1]}
                                value={
                                    <div onClick={onAddRealestateToAssembly(assemblyAddRealestate)}>
                                        {assemblyAddRealestate.number}
                                    </div>
                                }
                            />
                        </UI.List.Row>
                    ];
                    break;
                case ScreenSize.MOBILE:
                    listRows = [
                        <UI.List.Row
                            className={styles.AssemblyRealestateListRowMobile}
                            key={`row-mobile-${assemblyAddRealestate.id}`}
                        >
                            <div
                                className={styles.MobileCol}
                                onClick={onAddRealestateToAssembly(assemblyAddRealestate)}
                            >
                                <div className={styles.MobileTop}>
                                    <div className={styles.RealestateNames}>
                                        <strong>{assemblyAddRealestate.name}</strong>
                                    </div>
                                </div>
                                <div className={styles.MobileBottom}>
                                    <div className={styles.MobileLeft}>{assemblyAddRealestate.number}</div>
                                </div>
                            </div>
                        </UI.List.Row>
                    ];
                    break;
            }

            return (
                <UI.List.Item
                    key={`item-${assemblyAddRealestate.id}`}
                    screenSize={screenSize}
                    rows={listRows}
                    className={styles.ListItem}
                />
            );
        },
        [onAddRealestateToAssembly, columnWidths]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            if (screenSize === ScreenSize.MOBILE) {
                return <></>;
            }
            const rows = [
                <UI.List.Cell
                    key={"c-1"}
                    colspan={columnWidths[0]}
                    value={<Trans>screens.realestate.title</Trans>}
                    truncate
                />,
                <UI.List.Cell
                    key={"c-2"}
                    className={`${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP ? "d-none" : "d-flex"}`}
                    colspan={columnWidths[1]}
                    value={<Trans>screens.realestate.dashboard.number</Trans>}
                    truncate
                />
            ];
            return <UI.List.Header rows={rows} key={"r-1"} className="p-0" />;
        },
        [columnWidths, uiStore.currentScreenSize]
    );

    return (
        <div>
            <div style={{ width: "93%" }}>
                <UI.Input
                    type="search"
                    onChange={onSearch}
                    placeholder={t("labels.search").toString()}
                    value={searchQuery}
                />
            </div>
            <div className="mb-4" />
            <UI.List.BasicList
                screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                items={displayedRealestateItems}
                renderItem={renderItem}
                renderHeader={renderHeader}
                itemsPerPage={10}
                emptyListMessage={
                    assemblyStore.assemblies.length === 0
                        ? t("screens.assemblies.list.assemblies_are_loading").toString()
                        : t("screens.assemblies.list.no_assemblies_found").toString()
                }
            />
        </div>
    );
};

export const AddRealestateModalList = Sentry.withProfiler(observer(AddRealestateModalListBase));
